'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, AlertTriangle, Phone, Heart, Thermometer, Ear, Activity, CheckCircle } from 'lucide-react'

export default function FirstAidPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-red-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/safety"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          안전 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-red-900/30' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3 xxs:gap-2">
              <div className="p-2 xxs:p-1.5 rounded-lg bg-red-500">
                <Heart className="w-6 h-6 xxs:w-5 xxs:h-5 text-white" />
              </div>
              <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                프리다이빙 응급처치 가이드
              </h1>
            </div>
            <div className={`mt-3 xxs:mt-2 p-3 xxs:p-2 rounded-lg border-l-4 border-red-500 ${
              isDark ? 'bg-red-900/30' : 'bg-red-100'
            }`}>
              <p className={`text-sm xxs:text-xs font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                응급 상황에서는 신속한 대응이 생명을 구합니다. 이 가이드를 숙지하세요!
              </p>
            </div>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 주요 응급 상황 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                주요 응급 상황
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  {
                    icon: AlertTriangle,
                    title: '블랙아웃 (의식 상실)',
                    color: 'red',
                    items: ['즉시 수면으로 끌어올리기', '기도 확보 (턱 들기)', '얼굴에 물 뿌려 자극', '의식 회복 안되면 119 신고']
                  },
                  {
                    icon: Thermometer,
                    title: '저체온증',
                    color: 'blue',
                    items: ['즉시 물에서 나오기', '젖은 옷 벗기고 마른 옷으로 갈아입기', '담요로 몸 감싸기', '따뜻한 음료 제공 (술 X)', '심한 경우 119 신고']
                  },
                  {
                    icon: Ear,
                    title: '고막 손상',
                    color: 'purple',
                    items: ['즉시 다이빙 중단', '귀에 물이 들어가지 않도록 주의', '이비인후과 진료 받기', '완치될 때까지 다이빙 금지']
                  },
                  {
                    icon: Activity,
                    title: '근육 경련',
                    color: 'orange',
                    items: ['경련 부위 스트레칭', '부드럽게 마사지', '수면에서 휴식', '물 섭취로 탈수 방지']
                  },
                ].map((emergency, idx) => {
                  const colorClasses = {
                    red: { bg: isDark ? 'bg-red-900/20' : 'bg-red-50', text: isDark ? 'text-red-300' : 'text-red-800', icon: isDark ? 'text-red-400' : 'text-red-600' },
                    blue: { bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50', text: isDark ? 'text-blue-300' : 'text-blue-800', icon: isDark ? 'text-blue-400' : 'text-blue-600' },
                    purple: { bg: isDark ? 'bg-purple-900/20' : 'bg-purple-50', text: isDark ? 'text-purple-300' : 'text-purple-800', icon: isDark ? 'text-purple-400' : 'text-purple-600' },
                    orange: { bg: isDark ? 'bg-orange-900/20' : 'bg-orange-50', text: isDark ? 'text-orange-300' : 'text-orange-800', icon: isDark ? 'text-orange-400' : 'text-orange-600' },
                  }[emergency.color]!
                  return (
                    <div key={idx} className={`p-4 xxs:p-3 rounded-xl ${colorClasses.bg}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <emergency.icon className={`w-5 h-5 xxs:w-4 xxs:h-4 ${colorClasses.icon}`} />
                        <h3 className={`font-bold text-base xxs:text-sm ${colorClasses.text}`}>
                          {emergency.title}
                        </h3>
                      </div>
                      <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {emergency.items.map((item, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* CPR */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                CPR (심폐소생술)
              </h2>
              <p className={`text-sm xxs:text-xs mb-4 xxs:mb-3 ${isDark ? 'text-yellow-200/80' : 'text-yellow-800'}`}>
                의식이 없고 호흡이 없을 때 즉시 시행
              </p>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { step: '1', title: '119 신고', desc: '주변 사람에게 119 신고 요청' },
                  { step: '2', title: '가슴 압박', desc: '분당 100-120회, 깊이 5cm, 30회 실시' },
                  { step: '3', title: '인공호흡', desc: '2회 실시 (가능한 경우)' },
                  { step: '4', title: '반복', desc: '구급대 도착까지 30:2 비율로 반복' },
                ].map((item, idx) => (
                  <div key={idx} className={`p-3 xxs:p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-5 h-5 xxs:w-4 xxs:h-4 rounded-full flex items-center justify-center text-xs xxs:text-[10px] font-bold ${
                        isDark ? 'bg-yellow-500 text-black' : 'bg-yellow-500 text-white'
                      }`}>
                        {item.step}
                      </span>
                      <span className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.title}
                      </span>
                    </div>
                    <p className={`text-xs xxs:text-[10px] ml-7 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 중요 연락처 */}
            <div className={`p-4 xxs:p-3 rounded-xl border-l-4 border-red-500 ${
              isDark ? 'bg-red-900/20' : 'bg-red-50'
            }`}>
              <div className="flex items-start gap-3 xxs:gap-2">
                <Phone className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-red-300' : 'text-red-900'}`}>
                    중요 연락처
                  </h3>
                  <div className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                    <p><strong>긴급 구조:</strong> 119</p>
                    <p><strong>해양 긴급:</strong> 122 (해양경찰)</p>
                    <p><strong>응급 의료 상담:</strong> 129</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
